import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import { ObjectStatus } from "../../Core/Api/Types/object-status";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { RootState } from "../redux-store";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { SortingType } from "../../Core/Api/Types/sorting-type";

export interface StatusHistoryFilter {
  status: ObjectStatus;
  startDate?: Date;
  endDate?: Date;
}

export interface SortingInfos {
  type: SortingType;
  name: string;
  desc: boolean;
}

export interface moreInfosFilter {
  ids: number[];
  labels: string[];
}

export interface ReduxStateFilters {
  metadata: IDbBaseMetadataValueInfos[];
  classes: ObjectClass[] | null;
  types: ObjectType[] | null;
  statuses: ObjectStatus[] | null;
  statusHistory: StatusHistoryFilter[];
  titleCustomerIds: string[] | null;
  assetIds: number[] | null;
  parentObjectIds: number[] | null;
  hideWithoutAsset: boolean;
  confidentiality: boolean;
  createdBy: moreInfosFilter | null;
  providerIds: moreInfosFilter | null;
  pattern: string;
  limit: number | null;
  cachedFilters: { [key: string]: ReduxStateFilters };
  sort: SortingInfos | null;
}

const initialState: ReduxStateFilters = {
  metadata: [],
  pattern: "",
  classes: [],
  types: [],
  statuses: [],
  statusHistory: [],
  titleCustomerIds: null,
  assetIds: null,
  parentObjectIds: null,
  hideWithoutAsset: false,
  confidentiality: false,
  createdBy: null,
  providerIds: null,
  limit: null,
  cachedFilters: {},
  sort: null,
};

const delimiters: string[] = ["\n", " ", "|", ";", ","];

const filtersSlice = createSlice({
  name: "filters",
  initialState,

  reducers: {
    StoreFiltersState: (state, action) => {
      state.metadata = action.payload;
    },
    AddOrUpdateFilter: (state, action: PayloadAction<IDbBaseMetadataValueInfos>) => {
      if (action.payload.fieldType === BaseMetadataType.DbDateTimeMetadataValueInfos) {
        let exists: boolean =
          state.metadata.find((x) => x.id === action.payload.id && x.valueLabel === action.payload.valueLabel) !==
          undefined;
        if (!exists) state.metadata = [...state.metadata, action.payload];
        else
          state.metadata[
            state.metadata.findIndex((x) => x.id === action.payload.id && x.valueLabel === action.payload.valueLabel)
          ] = action.payload;
        return;
      }
      let exists: boolean = state.metadata.find((x) => x.id === action.payload.id) !== undefined;

      if (!exists) state.metadata = [...state.metadata, action.payload];
      else state.metadata[state.metadata.findIndex((x) => x.id === action.payload.id)] = action.payload;
    },
    AddOrUpdateStatusHistoryFilter: (state, action: PayloadAction<StatusHistoryFilter>) => {
      let exists = state.statusHistory?.find((x) => x.status === action.payload.status);

      if (!exists) state.statusHistory = [...state.statusHistory, action.payload];
      else
        state.statusHistory[state.statusHistory.findIndex((x) => x.status === action.payload.status)] = action.payload;
    },
    RemoveStatusHistoryFilter: (state, action: PayloadAction<ObjectStatus>) => {
      state.statusHistory = state.statusHistory.filter((x) => x.status !== action.payload);
    },
    AddOrUpdateSorting: (state, action: PayloadAction<SortingInfos>) => {
      state.sort = action.payload;
    },
    RemoveSorting: (state) => {
      state.sort = null;
    },
    RemoveFilterById: (state, action) => {
      state.metadata = state.metadata.filter((item) => item.id !== action.payload);
    },
    RemoveAllFilters: (state) => {
      state.metadata = [];
      state.statusHistory = [];
      state.classes = null;
      state.types = null;
      state.statuses = null;
      state.titleCustomerIds = null;
      state.assetIds = null;
      state.hideWithoutAsset = false;
      state.confidentiality = false;
      state.createdBy = null;
      state.providerIds = null;
      state.parentObjectIds = null;
    },
    SetSearchPattern: (state, action) => {
      state.pattern = action.payload;
    },
    SetClassFilter: (state, action) => {
      if (action.payload === "") state.classes = null;
      else state.classes = action.payload;
    },
    SetTypeFilter: (state, action) => {
      if (action.payload === "") state.types = null;
      else state.types = action.payload;
    },
    SetLimit: (state, action) => {
      state.limit = action.payload;
    },
    SetAssetStatusFilter: (state, action) => {
      if (action.payload === "") state.statuses = null;
      else state.statuses = action.payload;
    },
    SetTitleCustomerIdsFilter: (state, action) => {
      if (!action.payload || action.payload.length === 0) state.titleCustomerIds = null;
      else {
        let values: string[] = [];

        (action.payload as string[]).forEach((v) => {
          let isOk = false;
          for (let i = 0; i < delimiters.length; ++i) {
            if (v.includes(delimiters[i])) {
              let split = v.split(delimiters[i]);
              split.forEach((splitted) => {
                values.push(splitted);
              });

              isOk = true;
              break;
            }
          }

          if (!isOk) values.push(v);
        });

        state.titleCustomerIds = values;
      }
    },
    SetAssetIdsFilter: (state, action) => {
      if (!action.payload || action.payload.length === 0) state.assetIds = null;
      else {
        let values: number[] = [];

        (action.payload as string[]).forEach((v) => {
          let isOk = false;
          for (let i = 0; i < delimiters.length; ++i) {
            if (v.toString()?.includes(delimiters[i])) {
              let split = v.split(delimiters[i]);
              split.forEach((splitted) => {
                values.push(Number(splitted));
              });

              isOk = true;
              break;
            }
          }

          if (!isOk) values.push(Number(v));
        });

        state.assetIds = values;
      }
    },
    SetConfidentialityFilter: (state, action) => {
      state.confidentiality = action.payload;
    },
    SetHideWithoutAssetFilter: (state, action) => {
      state.hideWithoutAsset = action.payload;
    },
    SetCreatedbyFilter: (state, action) => {
      state.createdBy = action.payload;
    },
    SetProviderIdFilter: (state, action) => {
      state.providerIds = action.payload;
    },
    SetParentObjectIdsFilter: (state, action) => {
      state.parentObjectIds = action.payload;
    },
    ResetReduxData: (state) => {
      state = initialState;
    },
  },
});

export const {
  StoreFiltersState,
  AddOrUpdateFilter,
  RemoveFilterById,
  AddOrUpdateStatusHistoryFilter,
  RemoveStatusHistoryFilter,
  RemoveAllFilters,
  SetSearchPattern,
  SetClassFilter,
  SetTypeFilter,
  SetLimit,
  SetAssetIdsFilter,
  SetAssetStatusFilter,
  SetTitleCustomerIdsFilter,
  SetHideWithoutAssetFilter,
  SetConfidentialityFilter,
  SetCreatedbyFilter,
  SetProviderIdFilter,
  SetParentObjectIdsFilter,
  ResetReduxData,
} = filtersSlice.actions;

export const GetFilters = (state: RootState): IDbBaseMetadataValueInfos[] => state.filters.metadata;
export const GetLimit = (state: RootState): number | null => state.filters.limit;
export const GetTypeFilter = (state: RootState): ObjectType[] | null => state.filters.types;
export const GetClassFilter = (state: RootState): ObjectClass[] | null => state.filters.classes;
export const GetStatusHistoryFilter = (state: RootState): StatusHistoryFilter[] => state.filters.statusHistory;
export const GetAssetStatusFilter = (state: RootState): ObjectStatus[] | null => state.filters.statuses;
export const GetTitleCustomerIdsFilter = (state: RootState): string[] | null => state.filters.titleCustomerIds;
export const GetAssetIdsFilter = (state: RootState): number[] | null => state.filters.assetIds;
export const GetParentObjectIdsFilter = (state: RootState): number[] | null => state.filters.parentObjectIds;
export const GetConfidentialityFilter = (state: RootState): boolean => state.filters.confidentiality;
export const GetHideWithoutAssetFilter = (state: RootState): boolean => state.filters.hideWithoutAsset;
export const GetCreatedbyFilter = (state: RootState): moreInfosFilter | null => state.filters.createdBy;
export const GetProviderIdFilter = (state: RootState): moreInfosFilter | null => state.filters.providerIds;
export const GetPattern = (state: RootState): string => state.filters.pattern;

export default filtersSlice.reducer;
